import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import NewHeader from "@/components/NewHeader";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { getPageSEO, type SEOLanguage } from "@/data/seoTranslations";
import { getPathWithoutLanguage } from "@/utils/languageUtils";
import {
  getPrivacyPolicyCopy,
  PRIVACY_CONTACT_EMAIL,
} from "@/data/privacy-policy-i18n";

const PrivacyPolicy = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const seoLang = language as SEOLanguage;
  const seoData = useMemo(() => getPageSEO("privacyPolicy", seoLang), [seoLang]);
  const copy = useMemo(() => getPrivacyPolicyCopy(language), [language]);
  const canonicalPath = getPathWithoutLanguage(location.pathname);

  return (
    <div className="min-h-screen bg-white" data-oid="privacy-page-root">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={canonicalPath}
        language={seoLang}
      />
      <NewHeader lightBackground data-oid="privacy-header" />

      <main
        className="mx-auto max-w-3xl px-5 pb-24 pt-28 sm:px-8 sm:pt-32 lg:pb-32"
        data-oid="privacy-main"
      >
        <header className="border-b border-neutral-200 pb-10">
          <p className="font-lato text-[11px] font-semibold uppercase tracking-[0.28em] text-neutral-500">
            Beatenberg Resort &amp; Spa
          </p>
          <h1 className="mt-5 font-playfair text-3xl font-semibold tracking-tight text-neutral-900 sm:text-[2.125rem]">
            {copy.pageTitle}
          </h1>
          <p className="mt-4 font-lato text-sm text-neutral-600">
            {copy.lastUpdatedLabel}: {copy.lastUpdatedDate}
          </p>
        </header>

        <div className="mt-10 space-y-6 font-lato text-[15px] leading-[1.65] text-neutral-700">
          {copy.paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
          <p>
            <a
              href={`mailto:${PRIVACY_CONTACT_EMAIL}?subject=${encodeURIComponent(copy.pageTitle)}`}
              className="font-medium text-neutral-900 underline decoration-neutral-400 underline-offset-[5px] transition-colors hover:decoration-neutral-900"
            >
              {PRIVACY_CONTACT_EMAIL}
            </a>
          </p>
        </div>
      </main>

      <Footer data-oid="privacy-footer" />
    </div>
  );
};

export default PrivacyPolicy;
